/**
 * 계정 권한 판정 한 곳 모음 (2026-09-22 신설).
 *
 * ## 왜 모았는가
 *
 * 종전에는 화면마다 아래 한 줄을 복사해 썼다 —
 * `Layout.js` · `Payments.js` · `Credit.js` · `MessageTargetSetting.js` 4곳.
 *
 * ```js
 * const isAdmin = permissions?.[0]?.grant === "ADMIN" || permissions?.[0]?.grant === "OWNER"
 * ```
 *
 * 🔴 **`permissions[0]` 은 순서가 보장되지 않는다.**
 *    서버 `AccountPermissionService.findAllByAccountId` 에 `ORDER BY` 가 없었고
 *    (2026-09-22 에 `AccountPermission.id` 오름차순을 넣었다),
 *    한 채널은 권한 행을 여러 개 가진다(`Owner` · `Connect` · `Screen` · `Agent`).
 *    따라서 `[0]` 이 어느 행인지는 **DB 반환 순서**에 달려 있었고,
 *    같은 계정이 어느 날 갑자기 메뉴 절반을 잃을 수 있는 구조였다.
 *    ⇒ 순서를 고치는 것과 **별개로**, 판정 자체가 순서에 기대지 않아야 한다.
 *
 * 🔴 **대소문자.** `grant` 는 대문자가 정석이지만 서버 한 곳이 `"Admin"` 으로 넣고 있었다
 *    (`AccountController.authorize/kakao/token` 의 중복 계정 분기 — 2026-09-22 에 `"ADMIN"` 으로 고쳤다).
 *    운영 데이터에 그 값이 남아 있지 않은 것은 확인했지만, 비교를 대소문자 무시로 두어
 *    같은 실수가 화면을 조용히 망가뜨리지 않게 한다.
 *
 * ## 판정 축
 *
 * ⚠️ **등급(`grant`)이 아니라 서비스 종류(`serviceType`)가 주 축이다.**
 *    고객사는 실무상 admin 계정 하나로 서비스를 쓰므로 등급으로는 화면이 거의 갈리지 않는다
 *    (계정을 나눠 쓰는 상권용 계정 정도가 예외다).
 *    "이 고객이 이 기능을 쓸 수 있는가" 는 **무엇을 신청했는가**로 판정해야 한다.
 */

/** 관리 권한으로 인정하는 등급. */
const ADMIN_GRANTS = ["ADMIN", "OWNER"]

/** `grant` 를 비교 가능한 형태로 맞춘다(대소문자·공백 흡수). */
const normalizeGrant = (grant) => String(grant ?? "").trim().toUpperCase()

/** `serviceType` 을 비교 가능한 형태로 맞춘다 — 서버에 `Owner` 와 `owner` 가 섞여 있다. */
const normalizeServiceType = (serviceType) => String(serviceType ?? "").trim().toUpperCase()

/**
 * 현재 채널의 권한 행 목록. 채널을 못 찾으면 빈 배열이다.
 *
 * 🔑 **절대 예외를 던지지 않는다.** 이 앱에는 ErrorBoundary 가 한 곳도 없어서
 *    렌더 중 예외 한 번이면 `#root` 가 비어 **문구 없는 흰 화면**이 된다.
 *    `account` 가 `{}` 여도(=`GET /account` 실패 시 api 래퍼가 돌려주는 값) 안전해야 한다.
 */
export const channelPermissions = (account, channelId) =>
  account?.channels?.find((ch) => ch.id === channelId)?.permissions ?? []

/**
 * 이 채널에서 관리 권한을 가졌는가.
 *
 * 순서에 기대지 않고 **행 하나라도** 관리 등급이면 참으로 본다.
 * 종전 `[0]` 판정과 실질적으로 같은 결과를 내면서(채널 생성 시 `Owner/OWNER` 가 먼저 들어간다)
 * 행 순서가 바뀌어도 흔들리지 않는다.
 */
export const hasAdminGrant = (permissions) =>
  (permissions ?? []).some((pm) => ADMIN_GRANTS.includes(normalizeGrant(pm?.grant)))

/** 이 채널이 해당 서비스를 보유하는가(등급 무관 — 신청 대기 `PENDING` 도 포함된다). */
export const hasService = (permissions, serviceType) =>
  (permissions ?? []).some(
    (pm) => normalizeServiceType(pm?.serviceType) === normalizeServiceType(serviceType)
  )

/**
 * 이 채널이 해당 서비스를 **실제로 쓸 수 있는가**.
 *
 * `hasService` 와 다른 점 = 등급을 본다. 신청만 하고 아직 개통되지 않은 `PENDING`,
 * 해지 신청한 `TERMINATE` 는 거짓이다.
 * 메뉴 노출은 `hasService`, 기능 동작은 이 함수로 가른다.
 */
export const canUseService = (permissions, serviceType) =>
  (permissions ?? []).some(
    (pm) =>
      normalizeServiceType(pm?.serviceType) === normalizeServiceType(serviceType) &&
      ADMIN_GRANTS.includes(normalizeGrant(pm?.grant))
  )

/** `account_permission.service_type` 에 쓰이는 값들. */
export const SERVICE = {
  CONNECT: "Connect",
  SCREEN: "Screen",
  AGENT: "Agent",
  OWNER: "Owner",
  /**
   * 적립(포인트) 서비스 — 2026-09-22 신설.
   *
   * 🔴 **기존 운영 데이터에는 이 값이 없다.** 적립은 지금껏 아무 권한 판정 없이 열려 있었고
   *    (`/point/**` 는 서버에서도 `authenticated()` 뿐이었다), 그래서 신청하지 않은 고객도
   *    포인트 화면이 그냥 열렸다.
   *    ⇒ 이 값을 쓰려면 **이미 적립을 쓰고 있는 채널에 행을 넣어주는 마이그레이션이 먼저**다.
   *    마이그레이션 전에 기능을 막으면 운영 중인 고객이 끊긴다.
   */
  POINT: "Point",
}
